/**
 * Snapchat Ads — Async Stats Reports HTTP lifecycle (Story 19.8, Task 2).
 *
 * Snap's async stats reports are GET→Poll→Download:
 *   1. Create:   GET /v1/{entity}s/{id}/stats?async=true&async_format=csv → { report_run_id, async_status: "STARTED" }
 *   2. Poll:     GET /v1/{entity}s/{id}/stats_report?report_run_id=…       → { async_status, result? }
 *   3. Download: GET <signed CSV URL>  (24-hour validity, no auth header)
 *
 * Used when callers need >50 fields per request, level-`creative`/`media`
 * reports (sync only goes down to `ad`), reach overlap (cross-entity), or
 * date ranges that exceed Snap's per-granularity sync caps. Sync stats
 * (Task 1) covers the typical agency-sized fetches.
 *
 * Differences from Pinterest 19.5 + Microsoft Ads 19.2:
 *   - Async create is GET-with-flag, NOT POST.
 *   - Status enum is STARTED/RUNNING/COMPLETED/FAILED — no EXPIRED state
 *     (the report run stays COMPLETED indefinitely; only the signed `result`
 *     URL becomes stale at 24h).
 *   - CSV is plain `text/csv`, NOT zipped. No XLSX parser, no unzip.
 *   - 24-hour URL TTL — much longer than Pinterest's 5-minute or MS Ads' SAS-style.
 *
 * Security:
 *   - The signed download URL contains its own SAS-style signature; never log
 *     it directly. All errors flow through the typed
 *     `SnapchatAdsApiCallError` path which sanitises via `sanitizeErrorMessage`.
 *   - Pass `cf: { cacheTtl: 0, cacheEverything: false }` on download to
 *     prevent edge caching of mutable signed URLs (Pre-mortem F2 / B).
 *
 * @see https://developers.snap.com/api/marketing-api/Ads-API/measurement
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { SNAPCHAT_ADS_API_BASE, buildSnapchatHeaders } from "./api";
import { SnapchatAdsApiCallError, parseSnapchatAdsApiError } from "./errors";
import type {
  SnapchatAdsBreakdown,
  SnapchatAdsGranularity,
  SnapchatAdsReportDimension,
  SnapchatAttributionWindows,
} from "./analytics";

// ============================================================================
// Constants
// ============================================================================

/** Initial poll interval. Backoff multiplies until POLL_INTERVAL_MS_MAX. */
export const POLL_INTERVAL_MS_INITIAL = 2_000;
/** Cap on per-attempt poll interval. */
export const POLL_INTERVAL_MS_MAX = 10_000;
/** Backoff growth factor between successive polls. */
export const POLL_INTERVAL_MULTIPLIER = 1.5;
/** Default poll-attempt cap for typical reports (~2 minutes total budget). */
export const MAX_POLL_ATTEMPTS_SMALL = 60;
/** Extended poll-attempt cap for large reach-overlap reports (~5 minutes). */
export const MAX_POLL_ATTEMPTS_LARGE = 120;

export const CREATE_TIMEOUT_MS = 15_000;
export const POLL_TIMEOUT_MS = 10_000;
/** CSV downloads can be larger than Pinterest JSON — 60s cap. */
export const DOWNLOAD_TIMEOUT_MS = 60_000;

// ============================================================================
// Types
// ============================================================================

/** Snap's documented async-supporting entities. */
export type SnapchatAdsAsyncEntity =
  | "adaccount"
  | "campaign"
  | "adsquad"
  | "ad"
  | "creative"
  | "media";

/** Snap's documented async status enum — NO `EXPIRED` (unlike Pinterest). */
export type SnapchatAdsAsyncStatus =
  | "STARTED"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED";

/** Async output formats. We use `csv` exclusively (smaller, no binary parser). */
export type SnapchatAdsAsyncFormat = "csv" | "excel";

export interface SnapchatAdsAsyncCreateOptions extends SnapchatAttributionWindows {
  readonly startTime: string;
  readonly endTime: string;
  readonly granularity: SnapchatAdsGranularity;
  readonly fields: ReadonlyArray<string>;
  readonly reportDimension?: SnapchatAdsReportDimension;
  readonly breakdown?: SnapchatAdsBreakdown;
  readonly asyncFormat?: SnapchatAdsAsyncFormat;
  readonly omitEmpty?: boolean;
}

export interface SnapchatAdsAsyncCreateResponse {
  readonly request_status?: string;
  readonly request_id?: string;
  readonly report_run_id: string;
  readonly async_status: SnapchatAdsAsyncStatus;
}

export interface SnapchatAdsAsyncPollResponse {
  readonly async_status: SnapchatAdsAsyncStatus;
  readonly result?: string;
  readonly error_code?: string;
  readonly error_message?: string;
}

const ENTITY_PATH: Record<SnapchatAdsAsyncEntity, string> = {
  adaccount: "adaccounts",
  campaign: "campaigns",
  adsquad: "adsquads",
  ad: "ads",
  creative: "creatives",
  media: "media",
};

// ============================================================================
// Create (GET with `?async=true` — NOT POST, unlike Pinterest)
// ============================================================================

function appendCreateParams(
  url: URL,
  options: SnapchatAdsAsyncCreateOptions
): void {
  url.searchParams.set("async", "true");
  url.searchParams.set("async_format", options.asyncFormat ?? "csv");
  url.searchParams.set("granularity", options.granularity);
  url.searchParams.set("start_time", options.startTime);
  url.searchParams.set("end_time", options.endTime);
  url.searchParams.set("fields", options.fields.join(","));
  url.searchParams.set(
    "swipe_up_attribution_window",
    options.swipeUpAttributionWindow ?? "28_DAY"
  );
  url.searchParams.set(
    "view_attribution_window",
    options.viewAttributionWindow ?? "1_DAY"
  );
  url.searchParams.set(
    "conversion_source_types",
    options.conversionSourceTypes ?? "total"
  );
  url.searchParams.set(
    "action_report_time",
    options.actionReportTime ?? "conversion"
  );
  if (options.omitEmpty !== false) {
    url.searchParams.set("omit_empty", "true");
  }
  if (options.reportDimension !== undefined) {
    url.searchParams.set("report_dimension", options.reportDimension);
  }
  if (options.breakdown !== undefined) {
    url.searchParams.set("breakdown", options.breakdown);
  }
}

/**
 * Submits an async stats report job. Returns the `report_run_id` callers feed
 * into `pollSnapchatAdsAsyncReportUntilDone`. Pre-validates the field count
 * (Snap doesn't enforce the 50-field cap on async, but our orchestrator does
 * to keep the wire payload sensible — async typically used for >50 fields
 * via batched create-N-merge).
 *
 * Note: unlike Pinterest's POST-based create, Snap initiates async with a
 * GET + `?async=true` flag — verified web research, April 2026.
 */
export async function createSnapchatAdsAsyncStatsReport(
  accessToken: string,
  entity: SnapchatAdsAsyncEntity,
  entityId: string,
  options: SnapchatAdsAsyncCreateOptions
): Promise<SnapchatAdsAsyncCreateResponse> {
  if (!entityId) {
    throw new Error("Snapchat Ads async report requires an entity id");
  }
  if (options.fields.length === 0) {
    throw new Error("Snapchat Ads async report requires at least one field");
  }

  const segment = ENTITY_PATH[entity];
  const url = new URL(
    `${SNAPCHAT_ADS_API_BASE}/${segment}/${encodeURIComponent(entityId)}/stats`
  );
  appendCreateParams(url, options);

  const result = await safeFetch(
    url.toString(),
    { method: "GET", headers: buildSnapchatHeaders(accessToken) },
    CREATE_TIMEOUT_MS
  );
  if (!result.success) {
    throw buildAsyncApiError(result.error, "async report create");
  }
  let json: SnapchatAdsAsyncCreateResponse;
  try {
    json = (await result.data.json()) as SnapchatAdsAsyncCreateResponse;
  } catch {
    throw new SnapchatAdsApiCallError(
      {
        type: "unknown",
        message: "Snapchat Ads async report create response was not valid JSON",
        status: null,
        retryable: false,
      },
      "Snapchat Ads async report create"
    );
  }
  if (!json.report_run_id || typeof json.report_run_id !== "string") {
    throw new SnapchatAdsApiCallError(
      {
        type: "unknown",
        message:
          "Snapchat Ads async report create response missing report_run_id",
        status: null,
        retryable: false,
      },
      "Snapchat Ads async report create"
    );
  }
  return json;
}

// ============================================================================
// Poll (single attempt)
// ============================================================================

/** GET /v1/{entity}s/{id}/stats_report?report_run_id=… — single attempt. */
export async function pollSnapchatAdsAsyncReport(
  accessToken: string,
  entity: SnapchatAdsAsyncEntity,
  entityId: string,
  reportRunId: string
): Promise<SnapchatAdsAsyncPollResponse> {
  if (!reportRunId) {
    throw new Error("Snapchat Ads async report poll requires a report_run_id");
  }
  const segment = ENTITY_PATH[entity];
  const url = new URL(
    `${SNAPCHAT_ADS_API_BASE}/${segment}/${encodeURIComponent(entityId)}/stats_report`
  );
  url.searchParams.set("report_run_id", reportRunId);

  const result = await safeFetch(
    url.toString(),
    { method: "GET", headers: buildSnapchatHeaders(accessToken) },
    POLL_TIMEOUT_MS
  );
  if (!result.success) {
    throw buildAsyncApiError(result.error, "async report poll");
  }
  try {
    return (await result.data.json()) as SnapchatAdsAsyncPollResponse;
  } catch {
    throw new SnapchatAdsApiCallError(
      {
        type: "unknown",
        message: "Snapchat Ads async report poll response was not valid JSON",
        status: null,
        retryable: false,
      },
      "Snapchat Ads async report poll"
    );
  }
}

// ============================================================================
// Poll (backoff loop until terminal)
// ============================================================================

export interface PollUntilDoneOptions {
  readonly maxAttempts?: number;
  /** Test seam — defaults to `setTimeout`. */
  readonly sleepMs?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Polls until the report reaches a terminal status (COMPLETED/FAILED).
 * Returns the signed CSV download URL on COMPLETED. Throws typed
 * `SnapchatAdsApiCallError` on FAILED or poll-cap exhaustion.
 *
 * Backoff: 2s × 1.5^n capped at 10s. Default cap 60 attempts (~2 min);
 * reach-overlap callers pass `MAX_POLL_ATTEMPTS_LARGE = 120` for ~5 min.
 *
 * Snap doesn't have an `EXPIRED` status (unlike Pinterest). The report run
 * stays COMPLETED indefinitely; only the signed `result` URL becomes stale
 * at 24h. Stale-URL recovery is handled at the orchestrator layer (see
 * `runAsyncPipeline` in `snapchat-ads.ts`) by re-polling for a fresh URL.
 */
export async function pollSnapchatAdsAsyncReportUntilDone(
  accessToken: string,
  entity: SnapchatAdsAsyncEntity,
  entityId: string,
  reportRunId: string,
  options: PollUntilDoneOptions = {}
): Promise<string> {
  const maxAttempts = options.maxAttempts ?? MAX_POLL_ATTEMPTS_SMALL;
  const sleep = options.sleepMs ?? defaultSleep;

  let interval = POLL_INTERVAL_MS_INITIAL;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const response = await pollSnapchatAdsAsyncReport(
      accessToken,
      entity,
      entityId,
      reportRunId
    );
    if (response.async_status === "COMPLETED") {
      if (!response.result) {
        throw new SnapchatAdsApiCallError(
          {
            type: "unknown",
            message:
              "Snapchat Ads async report COMPLETED but no download URL was returned",
            status: null,
            retryable: false,
          },
          "Snapchat Ads async report poll"
        );
      }
      return response.result;
    }
    if (response.async_status === "FAILED") {
      const errorMessage = response.error_message
        ? `Snapchat Ads async report FAILED: ${response.error_message}`
        : "Snapchat Ads async report FAILED";
      throw new SnapchatAdsApiCallError(
        {
          type: "unknown",
          message: sanitizeErrorMessage(errorMessage),
          status: null,
          retryable: false,
        },
        "Snapchat Ads async report poll"
      );
    }
    // STARTED / RUNNING — wait and retry, unless this was the last attempt.
    if (attempt < maxAttempts) {
      await sleep(interval);
      interval = Math.min(
        Math.round(interval * POLL_INTERVAL_MULTIPLIER),
        POLL_INTERVAL_MS_MAX
      );
    }
  }
  throw new SnapchatAdsApiCallError(
    {
      type: "rate_limited",
      message: `Snapchat Ads async report poll exhausted after ${maxAttempts} attempts`,
      status: null,
      retryable: true,
    },
    "Snapchat Ads async report poll"
  );
}

// ============================================================================
// Download (signed URL, no auth header, CF cache disabled)
// ============================================================================

/**
 * Downloads the CSV body from the signed `result` URL.
 *
 * The URL contains its own SAS-style signature; we MUST NOT add an
 * Authorization header. Snap CSV downloads are plain `text/csv` (NOT zipped,
 * unlike MS Ads which mandates ZIP-CSV).
 *
 * Cloudflare cache is explicitly DISABLED via `cf.cacheTtl: 0` so a queue
 * retry that hits the same URL within 24h doesn't serve a stale earlier
 * response (Pre-mortem F-B / Failure B).
 *
 * Defensive: rejects unexpected `Content-Type` (CDN error pages can come
 * back as 200 OK with `text/html` — this catches that and surfaces a typed
 * error rather than feeding HTML into the CSV parser).
 *
 * Security: the URL is NEVER logged in error messages. Errors are routed
 * through `sanitizeErrorMessage` which redacts query params and tokens.
 */
export async function downloadSnapchatAdsAsyncReport(
  reportUrl: string
): Promise<string> {
  if (!reportUrl) {
    throw new Error("Snapchat Ads async report download requires a URL");
  }

  const init: RequestInit = {
    method: "GET",
    // Cloudflare-specific cache directives. Cast to RequestInit so non-Workers
    // runtimes (vitest jsdom) ignore the field gracefully.
    ...({ cf: { cacheTtl: 0, cacheEverything: false } } as object),
  };

  const result = await safeFetch(reportUrl, init, DOWNLOAD_TIMEOUT_MS);
  if (!result.success) {
    // Surface the error WITHOUT echoing the URL — sanitizeErrorMessage scrubs
    // tokens but the URL path itself can carry signature material.
    throw new SnapchatAdsApiCallError(
      {
        type: classifyDownloadStatus(result.error.providerStatus),
        message: sanitizeErrorMessage(
          `Snapchat Ads async report download failed (status ${result.error.providerStatus ?? "n/a"})`
        ),
        status: result.error.providerStatus ?? null,
        retryable:
          result.error.providerStatus !== undefined &&
          result.error.providerStatus >= 500,
      },
      "Snapchat Ads async report download"
    );
  }

  const contentType = result.data.headers.get("content-type") ?? "";
  if (
    contentType.length > 0 &&
    !/^text\/csv\b/i.test(contentType) &&
    !/^application\/octet-stream\b/i.test(contentType) &&
    !/^application\/csv\b/i.test(contentType)
  ) {
    throw new SnapchatAdsApiCallError(
      {
        type: "unknown",
        message: `Snapchat Ads async report download returned unexpected content-type: ${contentType.slice(0, 64)}`,
        status: null,
        retryable: false,
      },
      "Snapchat Ads async report download"
    );
  }

  try {
    return await result.data.text();
  } catch {
    throw new SnapchatAdsApiCallError(
      {
        type: "unknown",
        message: "Snapchat Ads async report download body could not be read",
        status: null,
        retryable: false,
      },
      "Snapchat Ads async report download"
    );
  }
}

function classifyDownloadStatus(
  status: number | undefined
):
  | "permission_denied"
  | "rate_limited"
  | "provider_unavailable"
  | "unknown" {
  if (status === 403) return "permission_denied";
  if (status === 429) return "rate_limited";
  if (status !== undefined && status >= 500) return "provider_unavailable";
  return "unknown";
}

// ============================================================================
// CSV parser (inline, ~30 lines — mirrors microsoft/reporting.ts)
// ============================================================================

/**
 * Parses Snap's async CSV body into `Array<Record<string, string>>`.
 *
 * Snap CSV format (verified web research, April 2026):
 *   - Column header on the FIRST line (no preamble like MS Ads).
 *   - Rows after that, each one entity-time-bucket.
 *   - No footer (no `@Copyright …` like MS Ads).
 *   - Quoted commas / RFC 4180 doubled-quote escapes supported.
 *
 * Returns raw string values; transformation to typed numbers happens in
 * `transformers.ts`. Defence-in-depth: returns `[]` on empty/corrupt input
 * rather than throwing.
 */
export function parseSnapchatAdsCsv(
  csvText: string
): Array<Record<string, string>> {
  if (!csvText) return [];

  const lines = csvText.split(/\r?\n/);
  if (lines.length < 2) return []; // header + at least 1 row

  const headerLine = lines[0];
  if (!headerLine) return [];
  const columns = parseCsvLine(headerLine);
  if (columns.length === 0) return [];

  const records: Array<Record<string, string>> = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (line == null) continue;
    if (line.trim() === "") continue;
    const cells = parseCsvLine(line);
    if (cells.length === 0) continue;
    const row: Record<string, string> = {};
    for (let c = 0; c < columns.length; c++) {
      const key = columns[c];
      if (!key) continue;
      row[key] = cells[c] ?? "";
    }
    records.push(row);
  }
  return records;
}

/**
 * RFC 4180 single-line CSV parser. Honours quoted commas and doubled-quote
 * escapes; doesn't handle multi-line quoted cells (Snap doesn't emit them
 * for the report types we use — campaign/ad names are single-line).
 */
function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === ",") {
      cells.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  cells.push(current);
  return cells.map((c) => c.trim());
}

// ============================================================================
// Internal: typed error builder shared by create + poll
// ============================================================================

function buildAsyncApiError(
  safeFetchError: {
    message: string;
    providerMessage?: string;
    providerStatus?: number;
  },
  label: string
): SnapchatAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON
    }
  }
  const parsed = parseSnapchatAdsApiError(
    parsedBody,
    safeFetchError.providerStatus
  );
  return new SnapchatAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: sanitizeErrorMessage(safeFetchError.message),
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    `Snapchat Ads ${label}`
  );
}
